// src/components/ui/CustomPagination.tsx
import { ChevronDown } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    itemsPerPageOptions?: number[];
}

export function CustomPagination({
    currentPage,
    totalItems,
    itemsPerPage,
    totalPages,
    onPageChange,
    onItemsPerPageChange,
    itemsPerPageOptions = [5, 10, 25, 50]
}: PaginationProps) {

    if (totalItems === 0) return null;

    const showPageButtons = totalItems > itemsPerPage && totalPages > 1;

    const startItem = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const getPageNumbers = (): (number | string)[] => {
        const maxVisiblePages = 5;
        const half = Math.floor(maxVisiblePages / 2);

        let start = Math.max(1, currentPage - half);
        const end = Math.min(totalPages, start + maxVisiblePages - 1);

        // Adjust start if we don't have enough pages at the end
        if (end - start + 1 < maxVisiblePages) {
            start = Math.max(1, end - maxVisiblePages + 1);
        }

        const pages: (number | string)[] = [];

        // Always show first page
        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...');
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // Always show last page
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }

        return pages;
    };


    return (
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-white/10">
            {/* Left side */}
            <div className="flex items-center space-x-4">
                <div className="text-sm text-slate-400">
                    Showing <span className="font-medium">{startItem}</span> to{' '}
                    <span className="font-medium">{endItem}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                </div>

                {/* ✅ Always visible */}
                {onItemsPerPageChange && (
                    <div className="relative hidden sm:block">
                        <select
                            value={itemsPerPage}
                            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                            className="appearance-none bg-black/20 border border-white/10 rounded-lg pl-3 pr-8 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500"
                        >
                            {itemsPerPageOptions.map(option => (
                                <option key={option} value={option}>
                                    {option} per page
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                )}
            </div>

            {/* Right side – pagination buttons */}
            {showPageButtons && (
                <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-slate-300 disabled:opacity-50"
                    >
                        Previous
                    </button>

                    {getPageNumbers().map((pageNum, index) =>
                        typeof pageNum === 'number' ? (
                            <button
                                key={pageNum}
                                onClick={() => onPageChange(pageNum)}
                                className={`w-10 h-10 rounded-lg ${currentPage === pageNum
                                    ? 'bg-cyan-600 text-white'
                                    : 'text-slate-300 hover:bg-white/10'
                                    }`}
                            >
                                {pageNum}
                            </button>
                        ) : (
                            <span key={`ellipsis-${index}`} className="px-2">
                                {pageNum}
                            </span>
                        )
                    )}

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-slate-300 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
